import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openLink } from '@tma.js/sdk-react';
import { ChevronDown, Crown, ExternalLink, Minus, Trophy } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { ChessReplay } from '@/components/chess/ChessReplay';
import { useGetChessChallengeQuery } from '@/services/qchessApi';
import type { Bracket, BracketSlot, BracketSlotStatus } from '@/types/qchess';
import { cn } from '@/lib/cn';

/**
 * Round name for a 0-based `roundIndex` in a bracket of `rounds` total rounds.
 * Returns an i18n key (+ optional `{ n }`) so the caller renders with its own
 * `t`. Final = last round; then Semifinal, Quarterfinal; earlier rounds are
 * "Round of N" where N = 2^(distance-from-final + 1).
 */
function roundLabelKey(
  roundIndex: number,
  rounds: number,
): { key: string; n?: number } {
  const fromEnd = rounds - 1 - roundIndex;
  if (fromEnd <= 0) return { key: 'bracket.round.final' };
  if (fromEnd === 1) return { key: 'bracket.round.semifinal' };
  if (fromEnd === 2) return { key: 'bracket.round.quarterfinal' };
  return { key: 'bracket.round.roundOf', n: Math.pow(2, fromEnd + 1) };
}

type MyState =
  | { kind: 'playing'; opponent: string | null; roundIndex: number; gameId: string | null }
  | { kind: 'waiting' }
  | { kind: 'eliminated' }
  | { kind: 'champion' }
  | { kind: 'none' };

/** Derive the signed-in player's current situation in the bracket. */
function computeMyState(
  b: Bracket,
  me: string | null,
  myGameId: string | null | undefined,
): MyState {
  if (!me) return { kind: 'none' };
  if (b.status === 'complete' && b.championLogtoUserId === me) return { kind: 'champion' };
  if (!b.participants.some((p) => p.logtoUserId === me)) return { kind: 'none' };

  for (const round of b.bracket) {
    for (const s of round) {
      if (s.status === 'in_progress' && (s.playerA === me || s.playerB === me)) {
        return {
          kind: 'playing',
          opponent: s.playerA === me ? s.playerB : s.playerA,
          roundIndex: s.roundIndex,
          gameId: myGameId ?? null,
        };
      }
    }
  }
  // A pending slot still holding me = I advanced and await the next opponent.
  for (const round of b.bracket) {
    for (const s of round) {
      if (s.status === 'pending' && (s.playerA === me || s.playerB === me)) {
        return { kind: 'waiting' };
      }
    }
  }
  if (b.status !== 'complete') {
    for (const round of b.bracket) {
      for (const s of round) {
        if (
          s.status === 'complete' &&
          (s.playerA === me || s.playerB === me) &&
          s.winner &&
          s.winner !== me
        ) {
          return { kind: 'eliminated' };
        }
      }
    }
  }
  return { kind: 'none' };
}

interface BracketViewProps {
  bracket: Bracket;
  me: string | null;
  /** currentGameId of my in-progress bracket match (from the challenges query). */
  myGameId?: string | null;
}

export function BracketView({ bracket, me, myGameId }: BracketViewProps) {
  const { t } = useTranslation('chess');

  const nameOf = useMemo(() => {
    const map = new Map(bracket.participants.map((p) => [p.logtoUserId, p.lichessUsername]));
    return (id: string | null): string => {
      if (id === null) return t('bracket.tbd');
      if (id === 'BYE') return t('bracket.bye');
      return map.get(id) || t('bracket.tbd');
    };
  }, [bracket.participants, t]);

  const myState = useMemo(
    () => computeMyState(bracket, me, myGameId),
    [bracket, me, myGameId],
  );

  const myUsername = me
    ? bracket.participants.find((p) => p.logtoUserId === me)?.lichessUsername
    : undefined;

  // Open the current round by default; let the user toggle the rest.
  const [openRounds, setOpenRounds] = useState<Set<number>>(
    () => new Set([bracket.currentRound]),
  );
  const toggle = (ri: number) =>
    setOpenRounds((prev) => {
      const next = new Set(prev);
      if (next.has(ri)) next.delete(ri);
      else next.add(ri);
      return next;
    });

  return (
    <div className="flex flex-col gap-3">
      <YourMatchCard state={myState} nameOf={nameOf} rounds={bracket.rounds} />

      {bracket.bracket.map((round, ri) => {
        const open = openRounds.has(ri);
        const done = round.filter((s) => s.status === 'complete' || s.status === 'bye').length;
        const rl = roundLabelKey(ri, bracket.rounds);
        return (
          <div key={ri} className="overflow-hidden rounded-xl bg-bg-secondary">
            <button
              type="button"
              onClick={() => toggle(ri)}
              className="flex w-full items-center justify-between px-3 py-2.5 text-left transition-opacity active:opacity-80"
            >
              <span className="text-sm font-bold text-fg">
                {t(rl.key, rl.n != null ? { n: rl.n } : undefined)}
              </span>
              <span className="flex items-center gap-2 font-mono text-[11px] text-fg-muted">
                {done}/{round.length}
                <ChevronDown
                  aria-hidden
                  className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
                />
              </span>
            </button>
            {open && (
              <div className="flex flex-col gap-1.5 px-3 pb-3">
                {round.map((slot) => (
                  <BracketSlotRow
                    key={slot.slotIndex}
                    slot={slot}
                    me={me}
                    nameOf={nameOf}
                    myUsername={myUsername}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function YourMatchCard({
  state,
  nameOf,
  rounds,
}: {
  state: MyState;
  nameOf: (id: string | null) => string;
  rounds: number;
}) {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();

  if (state.kind === 'none') return null;

  if (state.kind === 'champion') {
    return (
      <section className="flex items-center gap-3 rounded-xl bg-success/15 p-4 text-success">
        <Crown aria-hidden className="h-6 w-6 shrink-0" />
        <p className="text-base font-bold">{t('bracket.champion')} 🏆</p>
      </section>
    );
  }
  if (state.kind === 'eliminated') {
    return (
      <section className="flex items-center gap-3 rounded-xl bg-bg-tertiary p-4 text-fg-muted">
        <Minus aria-hidden className="h-5 w-5 shrink-0" />
        <p className="text-sm font-semibold">{t('bracket.eliminated')}</p>
      </section>
    );
  }
  if (state.kind === 'waiting') {
    return (
      <section className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-fg-muted">
        {t('bracket.waitingNextRound')}
      </section>
    );
  }
  // playing
  const rl = roundLabelKey(state.roundIndex, rounds);
  return (
    <section className="rounded-xl border border-brand/40 bg-brand/10 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-brand">
        {t('bracket.yourMatch')} · {t(rl.key, rl.n != null ? { n: rl.n } : undefined)}
      </p>
      <p className="mt-1 text-sm font-bold text-fg">
        <span className="text-fg-muted">{t('bracket.vs')} </span>
        {nameOf(state.opponent)}
      </p>
      {state.gameId ? (
        <button
          type="button"
          onClick={() => {
            haptics.impact('medium');
            openLink.ifAvailable(`https://lichess.org/${state.gameId}`);
          }}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-brand-fg transition-opacity active:opacity-80"
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
          {t('bracket.play')}
        </button>
      ) : (
        <p className="mt-2 text-[11px] text-fg-muted">{t('bracket.waitingNextRound')}</p>
      )}
    </section>
  );
}

function SlotStatusPill({ status }: { status: BracketSlotStatus }) {
  const { t } = useTranslation('chess');
  const tone: Record<BracketSlotStatus, string> = {
    pending: 'bg-bg-secondary text-fg-muted',
    in_progress: 'bg-success/15 text-success',
    complete: 'bg-brand/15 text-brand',
    bye: 'bg-fg-muted/15 text-fg-muted',
  };
  return (
    <span
      className={cn(
        'shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide',
        tone[status],
      )}
    >
      {t(`bracket.slot.${status}`)}
    </span>
  );
}

function PlayerLine({
  name,
  isWinner,
  isMe,
}: {
  name: string;
  isWinner: boolean;
  isMe: boolean;
}) {
  return (
    <div className="flex items-center gap-1.5">
      {isWinner && <Trophy aria-hidden className="h-3 w-3 shrink-0 text-brand" />}
      <span
        className={cn(
          'truncate text-sm',
          isWinner ? 'font-bold text-fg' : 'text-fg-muted',
          isMe && 'underline decoration-brand decoration-2 underline-offset-2',
        )}
      >
        {name}
      </span>
    </div>
  );
}

function BracketSlotRow({
  slot,
  me,
  nameOf,
  myUsername,
}: {
  slot: BracketSlot;
  me: string | null;
  nameOf: (id: string | null) => string;
  myUsername?: string;
}) {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();
  const [showReplay, setShowReplay] = useState(false);
  const canReplay = slot.status === 'complete' && !!slot.challengeId;

  return (
    <div className="rounded-lg bg-bg-tertiary px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <PlayerLine
            name={nameOf(slot.playerA)}
            isWinner={!!slot.winner && slot.winner === slot.playerA}
            isMe={!!me && slot.playerA === me}
          />
          <PlayerLine
            name={nameOf(slot.playerB)}
            isWinner={!!slot.winner && slot.winner === slot.playerB}
            isMe={!!me && slot.playerB === me}
          />
        </div>
        <SlotStatusPill status={slot.status} />
      </div>

      {canReplay && (
        <>
          <button
            type="button"
            onClick={() => {
              haptics.selection();
              setShowReplay((v) => !v);
            }}
            className="mt-2 text-[11px] font-semibold text-brand"
          >
            {showReplay ? t('bracket.hideReplay') : t('bracket.viewReplay')}
          </button>
          {showReplay && slot.challengeId && (
            <SlotReplay challengeId={slot.challengeId} myUsername={myUsername} />
          )}
        </>
      )}
    </div>
  );
}

function SlotReplay({
  challengeId,
  myUsername,
}: {
  challengeId: string;
  myUsername?: string;
}) {
  const { t } = useTranslation('chess');
  const { data: ch, isLoading } = useGetChessChallengeQuery(challengeId);

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader />
      </div>
    );
  }
  const gameIds = ch?.gameIds ?? [];
  if (gameIds.length === 0) return null;

  return (
    <div className="mt-2 flex flex-col gap-2">
      {gameIds.map((gid, i) => (
        <div key={gid} className="flex flex-col gap-1">
          {gameIds.length > 1 && (
            <span className="text-[11px] text-fg-muted">{t('bracket.match', { n: i + 1 })}</span>
          )}
          <ChessReplay gameId={gid} myUsername={myUsername} />
        </div>
      ))}
    </div>
  );
}
